// src/pages/SingleBlog.jsx
// Detail view for a single blog post.
// Reads the :id from the URL via useParams, fetches that blog from the backend,
// and renders its title, author, date, and body content.
//
// Three error states this page handles:
//   1. Loading: while the fetch is in-flight.
//   2. Not found: backend returned 404 (the ID doesn't exist).
//   3. Network error: backend is unreachable (different message + same shape).
//
// useEffect depends on `id` so navigating from /blogs/1 to /blogs/2 refetches.
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getBlogById } from "../api/blogs";

// Format ISO timestamp into "Apr 12, 2026" style. (Duplicated from Blogs.jsx;
// could be extracted to a shared utils file later if more pages need it.)
function formatDate(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso));
}

export default function SingleBlog() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorKind, setErrorKind] = useState(null); // "notfound" | "network" | null

  useEffect(() => {
    async function fetchBlog() {
      setLoading(true);
      setErrorKind(null);
      try {
        const response = await getBlogById(id);
        setBlog(response.data || null);
      } catch (err) {
        console.error("Failed to fetch blog:", err);
        if (err.response && err.response.status === 404) {
          setErrorKind("notfound");
        } else {
          setErrorKind("network");
        }
      } finally {
        setLoading(false);
      }
    }
    fetchBlog();
  }, [id]); // Re-run whenever the :id in the URL changes.

  return (
    <article className="max-w-wide mx-auto px-6 py-20 md:py-28">
      {/* Back link */}
      <Link
        to="/blogs"
        className="label-mono hover:text-ink transition-colors inline-block mb-12"
      >
        ← Back to Dispatches
      </Link>

      {/* Loading state */}
      {loading && (
        <p className="label-mono text-ink-faint">Loading dispatch…</p>
      )}

      {/* Not found state (real 404 from backend) */}
      {!loading && errorKind === "notfound" && (
        <div>
          <p className="label-mono mb-3">Filed under</p>
          <h1 className="font-display text-h2 leading-tight font-medium mb-6">
            No dispatch with this number.
          </h1>
          <p className="text-body text-ink-70 leading-relaxed max-w-2xl">
            The archive doesn't have a record at this ID. It may have been
            withdrawn, or the number may be wrong. The full archive is{" "}
            <Link to="/blogs" className="text-oxblood hover:underline">
              here
            </Link>
            .
          </p>
        </div>
      )}

      {/* Network error state */}
      {!loading && errorKind === "network" && (
        <p className="label-mono text-ink-faint">
          The archive is temporarily unavailable. Please check back shortly.
        </p>
      )}

      {/* Populated post */}
      {!loading && !errorKind && blog && (
        <>
          {/* Eyebrow: date and author */}
          <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-6 mb-8">
            <span className="label-mono">{formatDate(blog.created_at)}</span>
            <span className="hidden md:inline label-mono text-ink-30">·</span>
            <span className="label-mono">
              By {blog.user?.name || "Unknown"}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-display text-5xl md:text-display leading-tight md:leading-[1.05] font-medium tracking-tight mb-12 max-w-4xl">
            {blog.title}
          </h1>

          <hr className="hairline mb-12" />

          {/* Body content */}
          <div className="max-w-prose">
            <p className="text-body text-ink leading-relaxed whitespace-pre-line">
              {blog.content}
            </p>
          </div>
        </>
      )}
    </article>
  );
}
